import { Component, input, Input, OnInit, output } from '@angular/core';
import { ButtonType } from '../../../../store/subheader/type';
import { DocumentService } from '../../../../store/document/document.service';
import { InputByEntity } from '../../../../store/comment/types';
import {
  NotificationStatus,
  NotificationType,
} from '../../../../store/notification/types';
import { BehaviorSubject, Observable, withLatestFrom } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { documentActions } from '../../../../store/document/document.actions';
import { UploadFileService } from '../../../../store/upload-file/upload-file.service.facade';
import { NotificationService } from '../../../../store/notification/notification.service';
import { ConfigurationService } from '../../../../service/configuration.service';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';

@UntilDestroy()
@Component({
  selector: 'app-document-upload-drag-and-drop',
  templateUrl: './document-upload-drag-and-drop.component.html',
  styleUrl: './document-upload-drag-and-drop.component.scss',
  standalone: false,
})
export class DocumentUploadDragAndDropComponent implements OnInit {
  @Input({ required: true }) entityId: string;
  @Input({ required: true }) entityType: string;
  @Input() documentId: number = -1;
  @Input() multiple: boolean = false;
  @Input() button_label: string = 'DOCUMENT_LBL_ADD_DEFAULT';
  isDefaultUse = input<boolean>(true);
  showDescription = input<boolean>(true);
  isEditMode = input<boolean>(false);
  onAddedDocumentRefresh = output<boolean>();

  inputDocument: InputByEntity;
  uploadCompleted$: Observable<boolean>;

  fileOver$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  acceptFiles: string;

  protected readonly ButtonType = ButtonType;

  constructor(
    private documentService: DocumentService,
    private readonly uploadFileService: UploadFileService,
    private readonly notificationService: NotificationService,
    private readonly configurationService: ConfigurationService,
    private readonly translateService: TranslateService,
    private readonly store: Store,
  ) {}

  ngOnInit(): void {
    this.inputDocument = {
      entityId: +this.entityId,
      entityType: this.entityType,
    };

    //Quand l'upload est fini, on refresh la liste
    this.uploadCompleted$ = this.uploadFileService.completed$.pipe(
      withLatestFrom(this.uploadFileService.currentFileId$),
      filter(([_isInProgress, id]) => this.documentId == id),
      map(([isInProgress, _id]) => isInProgress),
      untilDestroyed(this),
    );

    this.uploadCompleted$.subscribe((x) => {
      if (x) {
        this.store.dispatch(documentActions.getByEntityId.refresh());
      }
      this.reset();
    });

    this.acceptFiles = this.configurationService
      .getAutorizedFileExtensions()
      .join(',');
  }

  // Handler for file drop
  onFileDrop(event: DragEvent): void {
    event.preventDefault();

    if (!this.isEditMode()) return;

    const files = event.dataTransfer?.files;

    if (!files) return;

    if (files.length > parseInt(this.configurationService.getMaxFiles())) {
      this.notificationService.add({
        timestamp: new Date().getTime(),
        content: this.translateService.instant('DOCUMENT_MSG_ERROR_MAX_FILES', {
          max: this.configurationService.getMaxFiles(),
        }),
        notificationStatus: NotificationStatus.Error,
        userCancelled: false,
        type: NotificationType.Toast,
        customNotification: false,
      });
      this.reset();
    }

    for (let i = 0; i < files.length; i++) {
      let file: File = files[i];

      if (file) {
        if (file.size == 0) {
          this.notificationService.add({
            timestamp: new Date().getTime(),
            content: this.translateService.instant(
              'DOCUMENT_MSG_ERROR_EMPTY_FILE',
              {
                fileName: file.name,
              },
            ),
            notificationStatus: NotificationStatus.Error,
            userCancelled: false,
            type: NotificationType.Toast,
            customNotification: false,
          });
          this.reset();
        } else if (
          file.size >
          parseInt(this.configurationService.getMaxSize()) * 1024 * 1024
        ) {
          this.notificationService.add({
            timestamp: new Date().getTime(),
            content: this.translateService.instant(
              'DOCUMENT_MSG_ERROR_MAX_SIZE',
              {
                fileName: file.name,
                max: this.configurationService.getMaxSize(),
              },
            ),
            notificationStatus: NotificationStatus.Error,
            userCancelled: false,
            type: NotificationType.Toast,
            customNotification: false,
          });
          this.reset();
        } else {
          this.uploadFileService.uploadFile(
            this.documentId,
            this.entityType,
            this.entityId,
            file,
          );
        }
      }
    }
    this.fileOver$.next(false);
  }

  // Prevent default dragover behavior
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (!this.isEditMode()) return;

    this.fileOver$.next(true);
  }

  onDragLeave($event: DragEvent) {
    if (!this.isEditMode()) return;

    this.fileOver$.next(false);
  }

  onDragEnd($event: DragEvent) {
    if (!this.isEditMode()) return;

    this.fileOver$.next(false);
  }

  cancelUpload = () => {
    this.uploadFileService.cancelUpload();
    this.reset();
  };

  reset = () => {
    // this.fileupload.nativeElement.value = '';
    this.uploadFileService.resetUpload();
  };

  delete = () => {
    // var message = this.translateService.instant(
    //   'WALLET_PASSENGER_DOCUMENT_DELETE'
    // );
    // this.messageService.openConfirmation(message).then((yes) => {
    //   if (yes) {
    //     this.walletService.deleteFile.call(this.document.id);
    //   }
    // });
  };

  refresh(newDocument: boolean) {
    if (newDocument && this.isDefaultUse()) {
      this.documentService.getByEntityId.call(this.inputDocument);
    }
    this.onAddedDocumentRefresh.emit(newDocument);
  }
}
